import type { FestivalConfig } from "./types";
import { FESTIVAL_ADDITIONS } from "./catalogue-additions";
import { applyFestivalReview } from "./catalogue-reviews";

const ORIGINAL_FESTIVALS: FestivalConfig[] = [
  // --- UK ---
  {
    slug: "reading-leeds",
    name: "Reading & Leeds",
    city: "Reading & Leeds",
    country: "UK",
    lat: 51.4607,
    lng: -0.9882,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 22, monthEnd: 8, dayEnd: 25 },
    confirmedDates: [
      { year: 2026, start: "2026-08-27", end: "2026-08-30" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-reading-leeds",
    genre: ["rock", "indie", "multi-genre", "hip-hop"],
    capacity: 90000,
    cardOneLiner:
      "The UK's biggest rock and indie weekender. Twin-site bank holiday institution since 1961.",
    labelOverrides: [
      "Bank holiday mudbath shaping up at both sites",
      "Richfield Avenue and Bramham Park will churn",
      "Typical bank holiday mud, manageable",
      "Ground holding well across the weekend",
      "Firmer than the bank holiday usually gets",
    ],
    blurb:
      "The same bank holiday weekend across two sites: Richfield Avenue in Reading and Bramham Park in Leeds. Ground at Reading turns fast near the river; Leeds heavy clay subsoil is less forgiving and earns the mud reputation in wet years.",
  },
  {
    slug: "boomtown",
    name: "Boomtown",
    city: "Winchester",
    country: "UK",
    lat: 51.0635,
    lng: -1.1660,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 8, monthEnd: 8, dayEnd: 13 },
    confirmedDates: [
      { year: 2026, start: "2026-08-12", end: "2026-08-16" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.1,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-boomtown",
    genre: ["multi-genre", "boutique", "electronic"],
    capacity: 76000,
    cardOneLiner:
      "Immersive theatrical world-building across 60+ stages. Genre-hopping from dub to drum and bass to punk.",
    labelOverrides: [
      "Matterley Bowl will collect it all",
      "The bowl fills, pitches turn treacherous",
      "Workable mud, the walk-out's the issue",
      "Firm enough to be a fair Boomtown",
      "A rare dry Matterley, savour it",
    ],
    blurb:
      "Matterley Bowl is a natural amphitheatre, which is beautiful and also means rain collects. Mud risk is high and the steep walk-out pitches make a bad weather weekend memorable.",
  },
  {
    slug: "download",
    name: "Download Festival",
    city: "Donington",
    country: "UK",
    lat: 52.8308,
    lng: -1.3758,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 6, dayStart: 11, monthEnd: 6, dayEnd: 14 },
    confirmedDates: [
      { year: 2026, start: "2026-06-10", end: "2026-06-14" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.1,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-download",
    genre: ["metal", "rock"],
    capacity: 111000,
    cardOneLiner:
      "The UK's premier heavy music festival. Three days of metal, rock and punk at Donington Park.",
    labelOverrides: [
      "2012 territory, brace yourselves",
      "Donington slopes will run as streams",
      "Mud patches, nothing Download hasn't seen",
      "Ground firm enough for headbanging in peace",
      "Dry Donington, a quiet gift",
    ],
    blurb:
      "Donington Park, early June. 2012 remains the benchmark muddy year; the ground there holds water and the campsites are on slopes that become slides when saturated.",
  },
  {
    slug: "parklife",
    name: "Parklife",
    city: "Manchester",
    country: "UK",
    lat: 53.5057,
    lng: -2.1841,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 6, dayStart: 13, monthEnd: 6, dayEnd: 14 },
    confirmedDates: [
      { year: 2026, start: "2026-06-20", end: "2026-06-21" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.9,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-parklife",
    genre: ["dance", "electronic", "hip-hop"],
    capacity: 80000,
    cardOneLiner:
      "Manchester's biggest weekender. Dance, hip-hop and electronic across Heaton Park.",
    labelOverrides: [
      "Heaton Park front-of-stage will be a bog",
      "Muddy pockets, rest of the park fine",
      "Standard Parklife patches, nothing wild",
      "Ground should hold well, urban drainage",
      "Dry grass and Manc sunshine, unreal",
    ],
    blurb:
      "Heaton Park, two days in mid-June. Urban park ground drains better than the farm-based UK festivals, but a wet weekend still pools badly at the front of stages.",
  },
  {
    slug: "boardmasters",
    name: "Boardmasters",
    city: "Newquay",
    country: "UK",
    lat: 50.4144,
    lng: -5.1005,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 7, monthEnd: 8, dayEnd: 11 },
    confirmedDates: [
      { year: 2026, start: "2026-08-05", end: "2026-08-09" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-boardmasters",
    genre: ["indie", "dance", "multi-genre"],
    capacity: 53000,
    cardOneLiner:
      "Surf by day, music by night on the Cornish coast. Fistral Beach meets headline acts.",
    labelOverrides: [
      "Atlantic front closing in, 2019 risk",
      "Serious winds off the bay, plan accordingly",
      "Cornish coast weather, watch the radar",
      "Mostly dry, a lucky Watergate week",
      "Clear skies and clean surf, rare",
    ],
    blurb:
      "Watergate Bay on the north Cornish coast. Atlantic storms and high winds are the real risk; the 2019 cancellation remains the cautionary tale.",
  },
  {
    slug: "latitude",
    name: "Latitude",
    city: "Henham Park",
    country: "UK",
    lat: 52.3408,
    lng: 1.5764,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 7, dayStart: 24, monthEnd: 7, dayEnd: 27 },
    confirmedDates: [
      { year: 2026, start: "2026-07-23", end: "2026-07-26" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.95,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-latitude",
    genre: ["indie", "multi-genre", "boutique"],
    capacity: 40000,
    cardOneLiner:
      "The UK's most arts-forward festival. Theatre, comedy, literature and music in the Suffolk countryside.",
    labelOverrides: [
      "A bad Suffolk summer writes Latitude off",
      "Boggy by the lake, rest of the park coping",
      "Some mud, nothing a Latitude regular minds",
      "Ground holding well, Suffolk summer behaving",
      "Glorious Henham Park, dry as it gets",
    ],
    blurb:
      "Henham Park in Suffolk, late July. Flatter and better-drained than most UK sites; mud risk is real but rarely catastrophic unless it's a particularly wet summer.",
  },
  {
    slug: "creamfields",
    name: "Creamfields",
    city: "Daresbury",
    country: "UK",
    lat: 53.3425,
    lng: -2.6356,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 22, monthEnd: 8, dayEnd: 25 },
    confirmedDates: [
      { year: 2026, start: "2026-08-27", end: "2026-08-30" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    relatedGuideSlug: "festival-bar-staff-jobs-uk",
    jobsFilter: "season=festival&location=fest-creamfields",
    genre: ["dance", "electronic"],
    capacity: 70000,
    cardOneLiner:
      "The UK's biggest dance music festival. Four days of electronic heavyweights on the Cheshire plains.",
    labelOverrides: [
      "Daresbury farmland will turn on the Friday",
      "Muddy patches, typical bank holiday weather",
      "Manageable mud, a working Creamfields",
      "Firm Daresbury ground, dry enough to dance",
      "A bone-dry Daresbury, lucky year",
    ],
    blurb:
      "Daresbury Estate, bank holiday weekend. Farmland site with mixed drainage; a dry week is firm but the ground fails fast once 20mm lands on it.",
  },
  {
    slug: "isle-of-wight",
    name: "Isle of Wight Festival",
    city: "Newport",
    country: "UK",
    lat: 50.6938,
    lng: -1.2908,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 6, dayStart: 18, monthEnd: 6, dayEnd: 21 },
    confirmedDates: [
      { year: 2026, start: "2026-06-18", end: "2026-06-21" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.95,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-isle-of-wight",
    genre: ["rock", "indie", "pop"],
    capacity: 55000,
    cardOneLiner: "Britain's original rock festival, revived. Hendrix played here in 1970; the legacy draws broad guitar bills.",
    labelOverrides: [
      "Island rain settling in for the weekend",
      "Wet patches, Seaclose coping for now",
      "Typical mid-June island weather, mixed",
      "Dry Seaclose Park, Solent breeze welcome",
      "Sunshine on the island, perfect festival",
    ],
    blurb: "Seaclose Park in Newport, Isle of Wight. Mid-June island weather is changeable; the Solent crossing adds a logistical layer and wind off the water keeps temperatures honest.",
  },
  {
    slug: "all-points-east",
    name: "All Points East",
    city: "London",
    country: "UK",
    lat: 51.5369,
    lng: -0.0393,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 5, dayStart: 23, monthEnd: 6, dayEnd: 1 },
    confirmedDates: [
      { year: 2026, start: "2026-05-22", end: "2026-05-31" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-all-points-east",
    genre: ["indie", "pop", "hip-hop", "rock"],
    capacity: 40000,
    cardOneLiner: "Ten days of curated headliner shows in east London. Victoria Park's answer to festival season.",
    labelOverrides: [
      "Northeast cell line closing in on the park",
      "Showers likely, Victoria Park handling it",
      "Changeable late May London, layers wise",
      "Dry Victoria Park, easy conditions",
      "Textbook late spring London, sunshine on the park",
    ],
    blurb: "Victoria Park in east London, spread across ten days in late May. The urban park drains well and late spring weather is generally kind, though afternoon thunder cells do blow through.",
  },
  {
    slug: "green-man",
    name: "Green Man",
    city: "Crickhowell",
    country: "UK",
    lat: 51.8700,
    lng: -3.1400,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 14, monthEnd: 8, dayEnd: 17 },
    confirmedDates: [
      { year: 2026, start: "2026-08-20", end: "2026-08-23" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-green-man",
    genre: ["indie", "boutique"],
    capacity: 25000,
    cardOneLiner: "Intimate indie and folk in the Brecon Beacons. Known for its lakeside setting and literary programme.",
    labelOverrides: [
      "Welsh valley flooding risk, streams up",
      "Steady rain, paths muddy, valley damp",
      "Typical Brecon August, damp but fine",
      "Dry valley, mountain views clear",
      "Glorious Brecon sunshine, rare treat",
    ],
    blurb: "Glanusk Park in the Brecon Beacons. Valley location traps moisture and the stream through the site can rise quickly after heavy rain. Mid-August Welsh weather is changeable.",
  },
  {
    slug: "shambala",
    name: "Shambala",
    city: "Kelmarsh",
    country: "UK",
    lat: 52.4000,
    lng: -0.9200,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 21, monthEnd: 8, dayEnd: 24 },
    confirmedDates: [
      { year: 2026, start: "2026-08-27", end: "2026-08-30" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.9,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-shambala",
    genre: ["multi-genre", "boutique", "electronic"],
    capacity: 15000,
    cardOneLiner: "Car-free, meat-free, ethics-first boutique festival. Fancy dress, world music and sustainability baked in.",
    labelOverrides: [
      "Bank holiday washout shaping up",
      "Muddy underfoot, parkland coping",
      "Typical late August, mixed but fine",
      "Dry Kelmarsh, parkland at its best",
      "Sun on the bank holiday, rare as it gets",
    ],
    blurb: "Kelmarsh Hall parkland in Northamptonshire. Small site with decent drainage on historic estate ground. Late August bank holiday weather is the main variable.",
  },
  {
    slug: "wireless",
    name: "Wireless",
    city: "London",
    country: "UK",
    lat: 51.5636,
    lng: -0.0517,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 7, dayStart: 11, monthEnd: 7, dayEnd: 13 },
    confirmedDates: [
      { year: 2026, start: "2026-07-10", end: "2026-07-12" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-wireless",
    genre: ["hip-hop", "electronic", "dance"],
    capacity: 50000,
    cardOneLiner: "London's biggest urban music festival. Hip-hop, grime and R&B heavyweights in Finsbury Park.",
    labelOverrides: [
      "London heat spike, concrete radiating",
      "Hot urban park, find the shade early",
      "Standard London July warmth",
      "Pleasant Finsbury Park conditions",
      "Cool London weekend, lucky draw",
    ],
    blurb: "Finsbury Park in north London. Urban park festival with limited shade. July heat in the city can build quickly with no sea breeze to break it.",
  },
  {
    slug: "trnsmt",
    name: "TRNSMT",
    city: "Glasgow",
    country: "UK",
    lat: 55.8497,
    lng: -4.2387,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 7, dayStart: 10, monthEnd: 7, dayEnd: 12 },
    confirmedDates: [
      { year: 2026, start: "2026-07-10", end: "2026-07-12" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.9,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-trnsmt",
    genre: ["rock", "indie", "pop"],
    capacity: 50000,
    cardOneLiner: "Glasgow's city-centre weekender. Rock, indie and pop headliners without the camping.",
    labelOverrides: [
      "Scottish summer at its worst, rain all weekend",
      "Showers and wind, typical Glasgow",
      "Mixed Glasgow weather, layers essential",
      "Dry Glasgow Green, rare summer treat",
      "Scottish sunshine, extraordinary",
    ],
    blurb: "Glasgow Green, a central city park. Scottish summer weather is highly variable; the urban setting means good drainage but wind channels along the Clyde.",
  },
  {
    slug: "end-of-the-road",
    name: "End of the Road",
    city: "Salisbury",
    country: "UK",
    lat: 51.0350,
    lng: -1.9200,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 8, dayStart: 29, monthEnd: 8, dayEnd: 31 },
    confirmedDates: [
      { year: 2026, start: "2026-09-03", end: "2026-09-06" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.95,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-end-of-the-road",
    genre: ["indie", "boutique"],
    capacity: 15000,
    cardOneLiner: "A curators' festival for the indie faithful. Small, intimate and reliably leftfield in the Dorset woods.",
    labelOverrides: [
      "Autumn arriving early, rain and cold",
      "Damp September start, wellies wise",
      "Changeable, typical early autumn Dorset",
      "Dry gardens, Indian summer territory",
      "Warm September sunshine, perfect send-off",
    ],
    blurb: "Larmer Tree Gardens on the Dorset/Wiltshire border. Early September timing means autumn can arrive during the festival; the woodland setting provides some shelter.",
  },
  {
    slug: "labyrinth-on-the-thames",
    name: "Labyrinth On The Thames",
    city: "Greenwich",
    country: "UK",
    lat: 51.483,
    lng: -0.006,
    region: "uk",
    timezone: "Europe/London",
    featured: true,
    featuredCopy:
      "The Old Royal Naval College is a UNESCO World Heritage Site and one of London's finest Baroque landmarks, designed by Christopher Wren. It rarely opens its grounds to music, let alone three weekends of electronic acts on the Thames waterfront. Labyrinth returns for its second year in 2026 after a sold-out debut.",
    featuredEdition: 2,
    lineup: ["Moby", "Dom Dolla", "Adriatique", "The Kooks", "Peggy Gou"],
    typicalWindow: { monthStart: 7, dayStart: 31, monthEnd: 8, dayEnd: 16 },
    confirmedDates: [
      {
        year: 2026,
        start: "2026-07-31",
        end: "2026-08-16",
        weekends: [
          { label: "Weekend 1", start: "2026-07-31", end: "2026-08-02" },
          { label: "Weekend 2", start: "2026-08-07", end: "2026-08-09" },
          { label: "Weekend 3", start: "2026-08-15", end: "2026-08-16" },
        ],
      },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-labyrinth-on-the-thames",
    genre: ["electronic", "dance"],
    capacity: 10000,
    cardOneLiner:
      "World-class electronic acts against a UNESCO riverside backdrop. Eight nights across three weekends.",
    labelOverrides: [
      "Thames storm cell closing in on the College",
      "Showers likely, riverside will be gusty",
      "Changeable London summer, layers advised",
      "Dry riverside evening, easy conditions",
      "Clear Thames skies, Wren buildings glowing",
    ],
    blurb:
      "Old Royal Naval College in Greenwich, a UNESCO World Heritage Site on the Thames. Summer storm cells and riverside gusts are the main risks across the three-weekend run.",
  },
  {
    slug: "labyrinth-tofte-manor",
    name: "Labyrinth Open Air",
    city: "Sharnbrook",
    country: "UK",
    lat: 52.227,
    lng: -0.558,
    region: "uk",
    timezone: "Europe/London",
    typicalWindow: { monthStart: 7, dayStart: 4, monthEnd: 7, dayEnd: 12 },
    confirmedDates: [
      {
        year: 2026,
        start: "2026-07-04",
        end: "2026-07-11",
        weekends: [
          { label: "Sasha & Digweed", start: "2026-07-04", end: "2026-07-04" },
          { label: "John Summit", start: "2026-07-11", end: "2026-07-11" },
        ],
      },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-labyrinth-tofte-manor",
    genre: ["electronic", "dance"],
    capacity: 3500,
    cardOneLiner:
      "Intimate open-air electronic events in 50 acres of 17th-century manor grounds.",
    labelOverrides: [
      "Bedfordshire storms heading for the manor",
      "Showers likely, gardens will be damp",
      "Changeable July, typical English summer",
      "Dry manor grounds, easy afternoon",
      "Clear Bedfordshire skies, perfect setting",
    ],
    blurb:
      "Tofte Manor in Bedfordshire, a 17th-century estate with 50 acres of grounds and two actual labyrinths. Individual event dates through July; English summer storms are the main weather risk.",
  },

  // --- Europe ---
  {
    slug: "tomorrowland",
    name: "Tomorrowland",
    city: "Boom",
    country: "Belgium",
    lat: 51.0900,
    lng: 4.3644,
    region: "europe",
    timezone: "Europe/Brussels",
    typicalWindow: { monthStart: 7, dayStart: 19, monthEnd: 7, dayEnd: 28 },
    confirmedDates: [
      {
        year: 2026,
        start: "2026-07-17",
        end: "2026-07-26",
        weekends: [
          { label: "Weekend 1", start: "2026-07-17", end: "2026-07-19" },
          { label: "Weekend 2", start: "2026-07-24", end: "2026-07-26" },
        ],
      },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-tomorrowland",
    genre: ["electronic", "dance"],
    capacity: 75000,
    cardOneLiner:
      "The world's most elaborate electronic festival. Fantasy-themed stages and 200+ DJs across two weekends.",
    labelOverrides: [
      "North Sea storm fronts stacking up",
      "Thunder cells probable across a weekend",
      "Belgian July, watch the afternoon radar",
      "Mostly dry De Schorre, kind weather",
      "Postcard Tomorrowland conditions",
    ],
    blurb:
      "Two back-to-back weekends at De Schorre in the Belgian lowlands. Late July thunderstorms blowing off the North Sea are the main weather risk, not heat.",
  },
  {
    slug: "sziget",
    name: "Sziget",
    city: "Budapest",
    country: "Hungary",
    lat: 47.5515,
    lng: 19.0516,
    region: "europe",
    timezone: "Europe/Budapest",
    typicalWindow: { monthStart: 8, dayStart: 7, monthEnd: 8, dayEnd: 12 },
    confirmedDates: [
      { year: 2026, start: "2026-08-11", end: "2026-08-15" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-sziget",
    genre: ["multi-genre", "rock", "indie", "electronic"],
    capacity: 90000,
    cardOneLiner:
      "A week-long city on a Danube island. 60+ stages spanning every genre, plus theatre, circus and art.",
    labelOverrides: [
      "35°C continental heat, no shade",
      "Hungarian summer peak, hydrate hard",
      "Hot, but manageable with river dips",
      "Warm Danube week, easier than most",
      "Unusually temperate Sziget, enjoy",
    ],
    blurb:
      "Óbudai-sziget, a Danube island in Budapest. Continental heat in early August regularly pushes 35°C, with almost no natural shade on the island.",
  },
  {
    slug: "primavera-sound",
    name: "Primavera Sound",
    city: "Barcelona",
    country: "Spain",
    lat: 41.4104,
    lng: 2.2210,
    region: "europe",
    timezone: "Europe/Madrid",
    typicalWindow: { monthStart: 5, dayStart: 29, monthEnd: 6, dayEnd: 8 },
    confirmedDates: [
      { year: 2026, start: "2026-06-04", end: "2026-06-06" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-primavera-sound",
    genre: ["indie", "rock", "electronic"],
    capacity: 100000,
    cardOneLiner:
      "Barcelona's tastemaker festival. Known for bold, genre-defying line-ups on the waterfront.",
    labelOverrides: [
      "Mediterranean heat wave hitting the concrete",
      "Baking Forum afternoons, shade scarce",
      "Warm and sticky, the sea breeze saves it",
      "Ideal Barcelona early summer weather",
      "Breezy Forum, textbook Primavera",
    ],
    blurb:
      "Parc del Fòrum on the Barcelona waterfront. Late May to early June Mediterranean heat with a sea breeze that either saves you or carries the humidity.",
  },
  {
    slug: "roskilde",
    name: "Roskilde",
    city: "Roskilde",
    country: "Denmark",
    lat: 55.6415,
    lng: 12.0804,
    region: "europe",
    timezone: "Europe/Copenhagen",
    typicalWindow: { monthStart: 6, dayStart: 28, monthEnd: 7, dayEnd: 5 },
    confirmedDates: [
      { year: 2026, start: "2026-06-27", end: "2026-07-04" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-roskilde",
    genre: ["multi-genre", "indie", "rock"],
    capacity: 130000,
    cardOneLiner:
      "Scandinavia's legendary non-profit festival since 1971. Eight days of music, art and activist culture.",
    labelOverrides: [
      "Danish summer's grim face, long wet week",
      "Serious mud risk, 8 days compounds it",
      "Mixed, long festival means streaks of both",
      "Dry spells across the week, a fair Roskilde",
      "Sun-soaked Danish July, unicorn year",
    ],
    blurb:
      "Dyrskuepladsen, a livestock-show ground outside Copenhagen. Danish summer swings hard; long dry spells and sudden heavy bursts can coexist inside one week.",
  },
  {
    slug: "rock-werchter",
    name: "Rock Werchter",
    city: "Werchter",
    country: "Belgium",
    lat: 50.9671,
    lng: 4.6994,
    region: "europe",
    timezone: "Europe/Brussels",
    typicalWindow: { monthStart: 7, dayStart: 3, monthEnd: 7, dayEnd: 6 },
    confirmedDates: [
      { year: 2026, start: "2026-07-02", end: "2026-07-05" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-rock-werchter",
    genre: ["rock", "indie", "multi-genre"],
    capacity: 88000,
    cardOneLiner:
      "Belgium's biggest rock festival. Four days of arena-scale headliners on immaculate grounds.",
    labelOverrides: [
      "Flat polder drowns in a single burst",
      "Werchter fields will swallow a boot",
      "Some muddy corners, site mostly coping",
      "Firm Werchter, pints on dry grass",
      "Bone-dry Werchter, once a decade",
    ],
    blurb:
      "Festivalpark Werchter, early July in the flat polders. Ground holds water and the site floods fast in a heavy afternoon.",
  },
  {
    slug: "mysteryland",
    name: "Mysteryland",
    city: "Haarlemmermeer",
    country: "Netherlands",
    lat: 52.3017,
    lng: 4.6934,
    region: "europe",
    timezone: "Europe/Amsterdam",
    typicalWindow: { monthStart: 8, dayStart: 28, monthEnd: 8, dayEnd: 30 },
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-mysteryland",
    genre: ["dance", "electronic"],
    capacity: 80000,
    cardOneLiner:
      "The world's longest-running electronic festival, since 1993. Immersive stage design in the Dutch countryside.",
    labelOverrides: [
      "Dutch heat peak, storms lurking",
      "Hot and humid, plan for shade",
      "Late August warmth, standard Mysteryland",
      "Comfortable Dutch summer conditions",
      "Cooler than usual, easy dancing weather",
    ],
    blurb:
      "Floriadeterrein, a former horticulture expo site. Late August Dutch heat is the usual risk; thunderstorms do appear, usually as sharp evening breaks.",
  },
  {
    slug: "awakenings",
    name: "Awakenings",
    city: "Hilvarenbeek",
    country: "Netherlands",
    lat: 51.5150,
    lng: 5.1417,
    region: "europe",
    timezone: "Europe/Amsterdam",
    typicalWindow: { monthStart: 7, dayStart: 4, monthEnd: 7, dayEnd: 7 },
    confirmedDates: [
      { year: 2026, start: "2026-07-10", end: "2026-07-12" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-awakenings",
    genre: ["electronic", "dance"],
    capacity: 60000,
    cardOneLiner:
      "Europe's flagship techno gathering. Three days in a safari-park forest with world-class sound systems.",
    labelOverrides: [
      "Dutch heatwave hammering the open stages",
      "Hot, lean on the forest shade",
      "Warm, the Beekse Bergen canopy helps",
      "Perfect forest techno weather",
      "Breezy and cool, a dream Awakenings",
    ],
    blurb:
      "Beekse Bergen, a safari-park forest. Shade is generous here, which matters in a Dutch heatwave; storms are the wildcard.",
  },
  {
    slug: "hellfest",
    name: "Hellfest",
    city: "Clisson",
    country: "France",
    lat: 47.0864,
    lng: -1.2806,
    region: "europe",
    timezone: "Europe/Paris",
    typicalWindow: { monthStart: 6, dayStart: 19, monthEnd: 6, dayEnd: 22 },
    confirmedDates: [
      { year: 2026, start: "2026-06-18", end: "2026-06-21" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-hellfest",
    genre: ["metal", "rock"],
    capacity: 60000,
    cardOneLiner:
      "France's answer to Wacken. 180,000 metalheads across two weekends in the Loire Valley.",
    labelOverrides: [
      "Loire heat peak, all-black becomes brutal",
      "Very hot, pack ice for the camp",
      "Warm, standard Clisson metal weather",
      "Classic early Loire summer, comfortable",
      "Mild Clisson, a merciful Hellfest",
    ],
    blurb:
      "Loire Valley, a week that regularly cooks. French June sun with limited shade on the site; the combination of heat and the all-black uniform is a proven test.",
  },
  {
    slug: "oya",
    name: "Øya",
    city: "Oslo",
    country: "Norway",
    lat: 59.9139,
    lng: 10.7522,
    region: "europe",
    timezone: "Europe/Oslo",
    typicalWindow: { monthStart: 8, dayStart: 5, monthEnd: 8, dayEnd: 9 },
    confirmedDates: [
      { year: 2026, start: "2026-08-12", end: "2026-08-15" },
    ],
    primaryRisk: "mud",
    groundModifier: 0.9,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-oya",
    genre: ["indie", "multi-genre"],
    capacity: 60000,
    cardOneLiner:
      "Oslo's eco-conscious city festival. Indie, alternative and world music in a central urban park.",
    labelOverrides: [
      "Scandinavian downpour week incoming",
      "Wet Oslo summer, parkland will churn",
      "Changeable, typical Nordic August",
      "Ground holding, Tøyenparken draining well",
      "A golden Oslo week, rare and good",
    ],
    blurb:
      "Tøyenparken in central Oslo. Scandinavian August is mild and changeable; the park drains reasonably well but can turn in a heavy afternoon.",
  },
  {
    slug: "lowlands",
    name: "Lowlands",
    city: "Biddinghuizen",
    country: "Netherlands",
    lat: 52.4531,
    lng: 5.6889,
    region: "europe",
    timezone: "Europe/Amsterdam",
    typicalWindow: { monthStart: 8, dayStart: 15, monthEnd: 8, dayEnd: 17 },
    confirmedDates: [
      { year: 2026, start: "2026-08-21", end: "2026-08-23" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.0,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-lowlands",
    genre: ["multi-genre", "indie", "rock", "electronic"],
    capacity: 60000,
    cardOneLiner:
      "The Netherlands' most eclectic festival. Music, film, science, comedy and art on a reclaimed polder.",
    labelOverrides: [
      "Flevoland water table rising, grim week",
      "Polder mud in full effect",
      "Workable Lowlands mud, hasn't defeated anyone",
      "Surprisingly firm polder, a lucky draw",
      "Dry Flevoland, the myth is real",
    ],
    blurb:
      "Flevoland polder, reclaimed land with a water table barely below the grass. Mid-August rain turns the site memorably fast.",
  },

  // --- North America ---
  {
    slug: "coachella",
    name: "Coachella",
    city: "Indio",
    country: "USA",
    lat: 33.6784,
    lng: -116.2372,
    region: "north-america",
    timezone: "America/Los_Angeles",
    typicalWindow: { monthStart: 4, dayStart: 10, monthEnd: 4, dayEnd: 19 },
    confirmedDates: [
      {
        year: 2026,
        start: "2026-04-10",
        end: "2026-04-19",
        weekends: [
          { label: "Weekend 1", start: "2026-04-10", end: "2026-04-12" },
          { label: "Weekend 2", start: "2026-04-17", end: "2026-04-19" },
        ],
      },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-coachella",
    genre: ["multi-genre", "pop", "indie", "hip-hop", "electronic"],
    capacity: 125000,
    cardOneLiner:
      "The festival that defined modern festival culture. Two weekends of genre-spanning headliners in the California desert.",
    labelOverrides: [
      "Desert heat territory, full-sun stages",
      "Very hot, shade becomes currency",
      "Typical Indio April, warm with a breeze",
      "Comfortable Coachella weather",
      "Picture-perfect desert spring",
    ],
    blurb:
      "Two long weekends in the Colorado Desert. Daytime highs regularly push past 35°C and the sun is relentless with almost no shade on the polo grounds.",
  },
  {
    slug: "lollapalooza",
    name: "Lollapalooza Chicago",
    city: "Chicago",
    country: "USA",
    lat: 41.8764,
    lng: -87.6189,
    region: "north-america",
    timezone: "America/Chicago",
    typicalWindow: { monthStart: 7, dayStart: 31, monthEnd: 8, dayEnd: 3 },
    confirmedDates: [
      { year: 2026, start: "2026-07-30", end: "2026-08-02" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-lollapalooza",
    genre: ["multi-genre", "rock", "pop", "hip-hop", "indie"],
    capacity: 100000,
    cardOneLiner:
      "Four days on Chicago's lakefront. Multi-genre heavyweight with 170+ acts across Grant Park.",
    labelOverrides: [
      "Midwest supercell risk, evac plan likely",
      "Serious storm chance, pack a plan",
      "Typical Grant Park weather, have layers",
      "Lakeshore staying dry, rare and welcome",
      "A clean four days in Grant Park",
    ],
    blurb:
      "Grant Park on the Lake Michigan shore. Late-afternoon Midwestern storms are the default risk, with at least one evacuation in most recent years.",
  },
  {
    slug: "bonnaroo",
    name: "Bonnaroo",
    city: "Manchester",
    country: "USA",
    lat: 35.4840,
    lng: -86.0857,
    region: "north-america",
    timezone: "America/Chicago",
    typicalWindow: { monthStart: 6, dayStart: 11, monthEnd: 6, dayEnd: 14 },
    confirmedDates: [
      { year: 2026, start: "2026-06-11", end: "2026-06-14" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-bonnaroo",
    genre: ["multi-genre", "rock", "indie", "electronic"],
    capacity: 80000,
    cardOneLiner:
      "Tennessee's communal camping festival. Four days of jam bands, rock, hip-hop and late-night sets on The Farm.",
    labelOverrides: [
      "Tennessee humidity peak, brutal wet heat",
      "Sticky southern summer at full tilt",
      "Classic Bonnaroo sweat-and-rinse weather",
      "Warm, humidity easing, good weekend",
      "Cool Tennessee rare-bird Bonnaroo",
    ],
    blurb:
      "Tennessee farmland in peak southern summer. Heat plus humidity is the primary hazard; the 2021 flooding cancellation is a reminder the ground fails too.",
  },
  {
    slug: "edc-las-vegas",
    name: "EDC Las Vegas",
    city: "Las Vegas",
    country: "USA",
    lat: 36.2719,
    lng: -115.0111,
    region: "north-america",
    timezone: "America/Los_Angeles",
    typicalWindow: { monthStart: 5, dayStart: 15, monthEnd: 5, dayEnd: 17 },
    confirmedDates: [
      { year: 2026, start: "2026-05-15", end: "2026-05-17" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-edc-las-vegas",
    genre: ["electronic", "dance"],
    capacity: 135000,
    cardOneLiner:
      "The world's biggest overnight dance festival. Carnival rides, 200+ DJs, dusk till dawn under the Nevada sky.",
    labelOverrides: [
      "Desert heat at full bore, even overnight",
      "Overnight lows in the 20s, no respite",
      "Warm Mojave nights, standard EDC fare",
      "Pleasant speedway conditions, breezy",
      "Cool desert nights, unusually kind",
    ],
    blurb:
      "Las Vegas Motor Speedway, overnight-only to dodge the desert sun. Overnight lows in the 20s and UV at walk-out are still the main concerns.",
  },
  {
    slug: "burning-man",
    name: "Burning Man",
    city: "Black Rock City",
    country: "USA",
    lat: 40.7864,
    lng: -119.2065,
    region: "north-america",
    timezone: "America/Los_Angeles",
    typicalWindow: { monthStart: 8, dayStart: 25, monthEnd: 9, dayEnd: 2 },
    confirmedDates: [
      { year: 2026, start: "2026-08-30", end: "2026-09-06" },
    ],
    primaryRisk: "dust",
    indicator: "dusty",
    jobsFilter: "season=festival&location=fest-burning-man",
    genre: ["boutique", "multi-genre", "electronic"],
    capacity: 80000,
    cardOneLiner:
      "A temporary city built on radical self-reliance. No spectators, no commerce, just art and community on the playa.",
    labelOverrides: [
      "Whiteout playa week, visibility zero",
      "Heavy playa dust, goggles mandatory",
      "Typical Black Rock dust, goggles fine",
      "Calmer playa, lucky ride",
      "Unusually still playa, treasure it",
    ],
    blurb:
      "A week on an alkaline playa at 1200 m altitude. Whiteout dust storms are the default; the 2023 rain event is the exception that proved the surface turns to glue when wet.",
  },
  {
    slug: "governors-ball",
    name: "Governors Ball",
    city: "New York",
    country: "USA",
    lat: 40.7829,
    lng: -73.9654,
    region: "north-america",
    timezone: "America/New_York",
    typicalWindow: { monthStart: 6, dayStart: 6, monthEnd: 6, dayEnd: 8 },
    confirmedDates: [
      { year: 2026, start: "2026-06-05", end: "2026-06-07" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-governors-ball",
    genre: ["multi-genre", "pop", "hip-hop", "indie"],
    capacity: 50000,
    cardOneLiner:
      "New York City's flagship outdoor festival. Three days of pop, hip-hop and indie at Flushing Meadows.",
    labelOverrides: [
      "Northeast storm line, delays near certain",
      "Humid NY heat plus thunder risk",
      "Typical Flushing Meadows June, watch radar",
      "Picture-perfect early June New York",
      "Clear Flushing Meadows skies, unusual",
    ],
    blurb:
      "Flushing Meadows, early June. Northeast thunderstorms and the humid New York heat are both plausible; at least one delayed set is near certain.",
  },
  {
    slug: "osheaga",
    name: "Osheaga",
    city: "Montreal",
    country: "Canada",
    lat: 45.5252,
    lng: -73.5364,
    region: "north-america",
    timezone: "America/Toronto",
    typicalWindow: { monthStart: 8, dayStart: 1, monthEnd: 8, dayEnd: 3 },
    confirmedDates: [
      { year: 2026, start: "2026-07-31", end: "2026-08-02" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-osheaga",
    genre: ["indie", "multi-genre", "pop"],
    capacity: 45000,
    cardOneLiner:
      "Montreal's island festival. Three days of indie, pop and electronic on Parc Jean-Drapeau in the St Lawrence.",
    labelOverrides: [
      "St Lawrence storm cells, island in the firing line",
      "Strong thunder risk, stages wind-exposed",
      "Montreal August, plan for a passing cell",
      "Dry Jean-Drapeau, crisp and warm",
      "Perfect Montreal summer weekend",
    ],
    blurb:
      "Parc Jean-Drapeau in the St Lawrence. Continental storms roll through on August afternoons; the island layout funnels wind across the stages.",
  },
  {
    slug: "electric-forest",
    name: "Electric Forest",
    city: "Rothbury",
    country: "USA",
    lat: 43.5403,
    lng: -86.2619,
    region: "north-america",
    timezone: "America/Detroit",
    typicalWindow: { monthStart: 6, dayStart: 19, monthEnd: 6, dayEnd: 22 },
    confirmedDates: [
      { year: 2026, start: "2026-06-25", end: "2026-06-28" },
    ],
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-electric-forest",
    genre: ["electronic", "dance", "indie"],
    capacity: 45000,
    cardOneLiner:
      "Illuminated woodland wonderland in Michigan. Electronic and jam in an enchanted pine forest.",
    labelOverrides: [
      "Severe cell risk, Sherwood can only do so much",
      "Thunder likely, pines help but don't fix",
      "Michigan summer flicker, manageable",
      "Tree-cover dry, a kind Forest",
      "Quiet Sherwood, golden weekend",
    ],
    blurb:
      "Sherwood Forest, a pine woodland in west Michigan. Tree cover handles light rain well; the storm cells that do break through are often violent.",
  },

  // --- ANZ ---
  {
    slug: "laneway",
    name: "Laneway Festival",
    city: "Sydney",
    country: "Australia",
    lat: -33.8688,
    lng: 151.2093,
    region: "anz",
    timezone: "Australia/Sydney",
    typicalWindow: { monthStart: 2, dayStart: 7, monthEnd: 2, dayEnd: 9 },
    confirmedDates: [
      { year: 2026, start: "2026-02-08", end: "2026-02-08" },
    ],
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-laneway",
    genre: ["indie", "multi-genre"],
    capacity: 25000,
    cardOneLiner:
      "Australia's premier indie circuit. Curated single-day bills touring multiple cities each February.",
    labelOverrides: [
      "Sydney heat peak, UV off the chart",
      "Very hot, southerly the only saviour",
      "Classic summer Sydney day",
      "Pleasant Centennial Park conditions",
      "Mild Sydney summer, lucky draw",
    ],
    blurb:
      "One-day touring festival in early February, Sydney's hottest stretch. Shade on the site is patchy and the southerly buster can swing conditions inside an hour.",
  },
  {
    slug: "splendour",
    name: "Splendour in the Grass",
    city: "Byron Bay",
    country: "Australia",
    lat: -28.6474,
    lng: 153.5972,
    region: "anz",
    timezone: "Australia/Sydney",
    typicalWindow: { monthStart: 7, dayStart: 24, monthEnd: 7, dayEnd: 26 },
    confirmedDates: [
      { year: 2026, start: "2026-07-17", end: "2026-07-19" },
    ],
    primaryRisk: "mud",
    groundModifier: 1.05,
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-splendour",
    genre: ["indie", "rock", "multi-genre", "electronic"],
    capacity: 50000,
    cardOneLiner:
      "Australia's biggest winter music festival. Indie, rock and electronic in the Byron Bay hinterland.",
    labelOverrides: [
      "2022 repeat territory, brace for deep mud",
      "Byron winter rain, ground failing fast",
      "Typical North Byron mud, navigable",
      "Firmer Parklands year, fair grounds",
      "Dry Byron winter, miraculous",
    ],
    blurb:
      "North Byron Parklands in the sub-tropics. Winter rain in a wet year makes this ground notoriously unusable; the 2022 mud is the benchmark.",
  },
  {
    slug: "beyond-the-valley",
    name: "Beyond The Valley",
    city: "Victoria",
    country: "Australia",
    lat: -38.5361,
    lng: 145.6000,
    region: "anz",
    timezone: "Australia/Melbourne",
    typicalWindow: { monthStart: 12, dayStart: 28, monthEnd: 12, dayEnd: 31 },
    primaryRisk: "heat",
    indicator: "hot",
    jobsFilter: "season=festival&location=fest-beyond-the-valley",
    genre: ["electronic", "dance", "pop"],
    capacity: 18000,
    cardOneLiner:
      "Victoria's New Year's Eve camping festival. Dance, electronic and pop to ring in the new year.",
    labelOverrides: [
      "Victorian heat spike, nights still cold",
      "Hot days, icy evenings, the swing catches everyone",
      "Warm New Year's Eve, standard Lardner",
      "Comfortable Victorian summer conditions",
      "Mild Lardner Park, rare Victoria",
    ],
    blurb:
      "Lardner Park, New Year's run in Victorian summer. Baking daytime heat with cold evenings; the swing catches people out every year.",
  },
  {
    slug: "rhythm-vines",
    name: "Rhythm and Vines",
    city: "Gisborne",
    country: "New Zealand",
    lat: -38.6623,
    lng: 177.9875,
    region: "anz",
    timezone: "Pacific/Auckland",
    typicalWindow: { monthStart: 12, dayStart: 29, monthEnd: 1, dayEnd: 1 },
    primaryRisk: "storms",
    indicator: "mixed",
    jobsFilter: "season=festival&location=fest-rhythm-vines",
    genre: ["electronic", "dance"],
    capacity: 25000,
    cardOneLiner:
      "New Zealand's New Year's festival. First in the world to see the sunrise, set among Gisborne's vineyards.",
    labelOverrides: [
      "Pacific low closing in for new year",
      "Storm cell probable, Waiohika exposed",
      "Changeable Gisborne NYE, watch the sky",
      "Clear Gisborne skies, sunrise safe",
      "Picture-perfect Waiohika NYE",
    ],
    blurb:
      "Waiohika Estate for the first sunrise of the new year. Late December NZ storms can blow in off the Pacific; it's otherwise a benign weather window.",
  },
];

export const FESTIVALS: FestivalConfig[] = [
  ...ORIGINAL_FESTIVALS.map(applyFestivalReview),
  ...FESTIVAL_ADDITIONS,
];

export function findFestival(slug: string): FestivalConfig | null {
  return FESTIVALS.find((f) => f.slug === slug) ?? null;
}
